// =============================================================================
// app/src/lib/ondeFica.ts — os lugares de cada peça, como o balcão os mostra.
//
// A folha "Onde fica" (componentes/OndeFica.tsx) abre por cima da Venda, da
// Busca, do catálogo e da ficha: ver o lugar custa um toque e não perde o
// carrinho nem o texto digitado (desenho "Onde fica", 06/10/2026). Aqui ficam
// as contas puras dela — o que conta como "ter lugar", a ordem dos lugares de
// uma peça e a ordem da caminhada pela loja —, para o teste conferir sem tela.
// =============================================================================
import { AREAS } from './enderecos';
import type { AreaDaMarca } from './marcasDasCalotas';
import { zonaDoLocal } from './zonas';

/** Os lugares de UMA peça: o principal (stock.local) e os extras (092). */
export interface LugaresDaPeca {
  principal: string | null;
  extras: string[];
  /**
   * Calota (15/10/2026): a área da marca dela. Quando existe, É o lugar — o
   * principal e os extras ficam só de reserva, porque gancho de calota gira.
   */
  area?: AreaDaMarca;
}

/** Um lugar pronto para a tela: o código e se ele é um EXTRA da peça. */
export interface LugarDaPeca {
  codigo: string;
  extra: boolean;
}

/**
 * Tem lugar no mapa? Vazio e "loja" não são lugar: "loja" é o que o cadastro
 * antigo gravava para "está em algum canto" (44 peças em 06/10/2026), e uma
 * faixa vermelha com "loja" dentro prometeria um lugar que não existe.
 */
export function temLugar(local: string | null | undefined): local is string {
  const t = (local ?? '').trim();
  return t !== '' && t.toLowerCase() !== 'loja';
}

/**
 * Junta as duas consultas de lugaresDasPecas (stock e stock_lugares) num mapa
 * por peça. Peça sem linha de estoque e sem extra não entra: quem pergunta
 * trata a ausência como "sem lugar". Os extras saem em ordem de código, como a
 * ficha sempre mostrou.
 */
export function montarLugares(
  principais: ReadonlyArray<{ product_id: string; local: string | null }>,
  extras: ReadonlyArray<{ product_id: string; local: string }>,
): Map<string, LugaresDaPeca> {
  const m = new Map<string, LugaresDaPeca>();
  for (const p of principais) m.set(p.product_id, { principal: p.local, extras: [] });
  for (const e of extras) {
    const atual = m.get(e.product_id) ?? { principal: null, extras: [] };
    if (!atual.extras.includes(e.local)) atual.extras.push(e.local);
    m.set(e.product_id, atual);
  }
  for (const l of m.values()) l.extras.sort((a, b) => a.localeCompare(b));
  return m;
}

/**
 * Os lugares que a tela mostra, o principal primeiro. "loja" e vazio caem
 * fora; o extra igual ao principal também (seria o mesmo lugar duas vezes).
 */
export function lugaresParaMostrar(
  principal: string | null | undefined,
  extras: readonly string[] = [],
): LugarDaPeca[] {
  const saida: LugarDaPeca[] = [];
  if (temLugar(principal)) saida.push({ codigo: principal.trim(), extra: false });
  for (const e of extras) {
    if (!temLugar(e)) continue;
    if (saida.some((l) => l.codigo.toUpperCase() === e.trim().toUpperCase())) continue;
    saida.push({ codigo: e.trim(), extra: true });
  }
  return saida;
}

// A ordem em que se anda pela loja: a frente (Z1), os corredores (Z2), os
// fundos (Z3), o 2º andar (Z4) e o sótão (Z5). É a ordem das ÁREAS da regra
// dos endereços, e o mapa sabe a zona de cada uma.
const ORDEM_DAS_ZONAS = Object.values(AREAS).map((a) => a.zona as string);

/** O endereço sem o TIPO na frente: "ES-CO-1D-01" → "CO-1D-01" — o lugar manda, não o móvel. */
function semTipo(codigo: string): string {
  const p = codigo.trim().toUpperCase().split(/\s*-\s*/).filter(Boolean);
  return p.length >= 2 && p[1]! in AREAS ? p.slice(1).join('-') : p.join('-');
}

/**
 * A lista "Onde buscar tudo" do carrinho, na ordem da caminhada: primeiro a
 * área (frente, corredor, fundos, andar de cima), depois o lugar dentro dela
 * ("corredor 1" antes de "corredor 2", seja estante ou chão) e por fim o
 * código inteiro. Código que o mapa não reconhece vai para o fim — ainda
 * aparece, só não atrapalha a volta.
 */
export function ordemDeCaminhada<T extends { codigo: string }>(itens: readonly T[]): T[] {
  const posicao = (codigo: string) => {
    const z = zonaDoLocal(codigo);
    const i = z ? ORDEM_DAS_ZONAS.indexOf(z) : -1;
    return i === -1 ? ORDEM_DAS_ZONAS.length : i;
  };
  const comparar = (a: string, b: string) => a.localeCompare(b, 'pt-BR', { numeric: true });
  return [...itens].sort((a, b) =>
    posicao(a.codigo) - posicao(b.codigo)
    || comparar(semTipo(a.codigo), semTipo(b.codigo))
    || comparar(a.codigo, b.codigo));
}
